import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../contexts/LanguageContext';

interface TreatmentItemProps {
  title: string;
  description?: string;
}

const TreatmentItem: React.FC<TreatmentItemProps> = ({ title, description }) => {
  return (
    <div className="py-3 px-4 bg-gray-50 border-b border-gray-200 last:border-b-0">
      <h3 className="text-lg font-medium text-gray-800">{title}</h3>
      {description && <p className="mt-1 text-gray-600">{description}</p>}
    </div>
  );
};

interface TreatmentCategoryProps {
  title: string;
  description: string;
  treatments: TreatmentItemProps[];
  icon: React.ReactNode;
}

const TreatmentCategory: React.FC<TreatmentCategoryProps> = ({
  title,
  description,
  treatments,
  icon
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="mb-6 bg-white rounded-lg shadow-md overflow-hidden transform transition-all duration-300 hover:shadow-lg">
      <button
        className="w-full flex items-start p-6 focus:outline-none focus:ring-2 focus:ring-light-blue"
        onClick={toggleExpand}
        aria-expanded={isExpanded}
        aria-controls={`treatment-${title.replace(/\s+/g, '-').toLowerCase()}`}
      >
        <div className="flex-1">
          <div className="flex items-center">
            <div className="mr-4 text-navy">
              {icon}
            </div>
            <h2 className="text-xl font-bold text-navy">{title}</h2>
          </div>
          <p className="mt-2 text-gray-700">{description}</p>
        </div>
        <ChevronDownIcon
          className={`h-6 w-6 text-navy transform transition-transform duration-200 ${
            isExpanded ? 'rotate-180' : ''
          }`}
        />
      </button>

      <div
        id={`treatment-${title.replace(/\s+/g, '-').toLowerCase()}`}
        className={`overflow-hidden transition-all duration-300 ${
          isExpanded ? 'max-h-[1000px]' : 'max-h-0'
        }`}
      >
        <div className="divide-y divide-gray-200">
          {treatments.map((treatment, idx) => (
            <TreatmentItem
              key={idx}
              title={treatment.title}
              description={treatment.description}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const BehandlungenSection: React.FC = () => {
  const { translations } = useLanguage();
  
  // Verwenden der Übersetzungen aus den Übersetzungsdateien
  return (
    <section className="py-20 bg-gray-50" id="behandlungen">
      <div className="container-custom">
        <div className="text-center mb-12">
          <h2 className="text-navy mb-4 text-2xl md:text-3xl font-bold">{translations.treatments.title}</h2>
          <p className="text-gray-700 max-w-3xl mx-auto">
            {translations.treatments.intro}
          </p>
        </div>

        <div className="space-y-6 animate-[fadeIn_500ms_ease]">
          {translations.treatments.categories.map((category, index) => (
            <TreatmentCategory
              key={index}
              title={category.title}
              description={category.description}
              treatments={category.treatments}
              icon={getCategoryIcon(index)}
            />
          ))}
        </div>

        <div className="text-center mt-12">
          <Link to="/kontakt?booking=true" className="btn btn-primary">
            {translations.treatments.cta}
          </Link>
        </div>
      </div>
    </section>
  );
};

// Helper-Funktion, um das passende Icon für jede Kategorie zu liefern
const getCategoryIcon = (index: number) => {
  const icons = [
    // Routine/Preventive
    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>,
    
    // Pain Therapy
    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>,
    
    // Periodontitis
    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
    </svg>,
    
    // Aesthetics
    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>,
    
    // Implants
    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
    </svg>
  ];
  
  return icons[index] || icons[0]; // Fallback zum ersten Icon wenn der Index zu groß ist
};

export default BehandlungenSection; 